import { useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter(n => !n.read).length;

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch { /* ignore */ }
  };

  const toneClass = (t) => ({
    payment_pending:            "border-l-4 border-l-orange-400 bg-orange-50",
    payment_overdue:            "border-l-4 border-l-rose-500 bg-rose-50",
    payment_success:            "border-l-4 border-l-emerald-400 bg-emerald-50",
    welcome:                    "border-l-4 border-l-primary bg-primary/5",
    onboarded:                  "border-l-4 border-l-secondary bg-secondary/10",
    new_chef:                   "border-l-4 border-l-secondary bg-secondary/10",
    chef_leave:                 "border-l-4 border-l-rose-400 bg-rose-50",
    chef_arriving:              "border-l-4 border-l-indigo-400 bg-indigo-50",
    subscription_requested:     "border-l-4 border-l-emerald-400 bg-emerald-50",
    subscription_cancelled:     "border-l-4 border-l-rose-400 bg-rose-50",
    complaint_filed:            "border-l-4 border-l-amber-400 bg-amber-50",
    slot_change_requested:      "border-l-4 border-l-indigo-400 bg-indigo-50",
    chef_change_requested:      "border-l-4 border-l-purple-400 bg-purple-50",
    customer_unavailability:    "border-l-4 border-l-cyan-400 bg-cyan-50",
    cancellation_warning:       "border-l-4 border-l-rose-500 bg-rose-100",
    chef_rating:                "border-l-4 border-l-amber-400 bg-amber-50",
  }[t] || "border-l-4 border-l-border bg-muted");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center transition-colors" data-testid="notif-bell" aria-label="Notifications">
          <Bell size={18} strokeWidth={1.75}/>
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="heading font-semibold">Notifications</div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="text-xs h-auto py-1 px-2" data-testid="notif-mark-all">
              <CheckCheck size={14} className="mr-1"/> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No notifications yet.</div>
          ) : items.map(n => (
            <div key={n.id} className={`p-3 ${toneClass(n.type)} ${n.read ? "opacity-70" : ""} border-b border-border last:border-0`} data-testid={`notif-${n.id}`}>
              <div className="text-sm font-semibold">{n.title}</div>
              {n.body && <div className="text-xs text-muted-foreground mt-1">{n.body}</div>}
              <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
